import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Check, LogOut, Mail, Package, Undo2 } from "lucide-react";
import Logo from "./components/Logo";
import { useAuth } from "./AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REPLY_TEMPLATES = [
  { key: "stitching", label: "I'm stitching it!", preview: "Your order is officially on my hook…" },
  { key: "ready", label: "It's ready!", preview: "Ta-da! Your order is finished and looking oh-so-cozy…" },
  { key: "thanks", label: "A little thank-you", preview: "Making your order brought me so much joy…" },
];

export default function OrdersInbox() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState(null);
  const [showDone, setShowDone] = useState(false);
  const [busy, setBusy] = useState(null);
  const [replyingFor, setReplyingFor] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/orders`, { withCredentials: true })
      .then((r) => setOrders(r.data))
      .catch(() => setOrders([]));
  }, []);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } finally {
      setUser(false);
      navigate("/login");
    }
  };

  const setDone = async (id, done) => {
    if (busy) return;
    setBusy(id);
    try {
      await axios.patch(`${API}/orders/${id}/status`, { done }, { withCredentials: true });
      setOrders((list) => list.map((o) => (o.id === id ? { ...o, done } : o)));
      toast.success(done ? "Marked done — nice stitching!" : "Moved back to open orders");
    } catch {
      toast.error("Couldn't update that order. Try again.");
    } finally {
      setBusy(null);
    }
  };

  const sendReply = async (order, templateKey) => {
    if (busy) return;
    setBusy(order.id);
    try {
      await axios.post(`${API}/orders/${order.id}/reply`, { template: templateKey }, { withCredentials: true });
      toast.success(`Sweet reply sent to ${order.name}!`);
      setReplyingFor(null);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg =
        typeof detail === "string" && detail.length < 120 && !detail.includes("<")
          ? detail
          : "Couldn't send that reply right now. Try again in a moment.";
      toast.error(msg);
    } finally {
      setBusy(null);
    }
  };

  const openOrders = orders?.filter((o) => !o.done) ?? [];
  const doneOrders = orders?.filter((o) => o.done) ?? [];
  const visible = showDone ? doneOrders : openOrders;

  return (
    <div className="min-h-screen bg-[#F9F6F0]" data-testid="orders-inbox">
      <header className="bg-[#F9F6F0]/80 backdrop-blur-xl border-b border-[#D3D7CF]/60 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size={34} />
            <span className="font-heading font-semibold text-lg text-[#2C402B]">Order Inbox</span>
          </div>
          <button
            onClick={logout}
            data-testid="logout-button"
            className="flex items-center gap-2 rounded-full border-2 border-[#2C402B] text-[#2C402B] px-4 py-2 text-sm font-heading font-medium transition-[background-color,color,transform] duration-300 hover:bg-[#2C402B] hover:text-[#F9F6F0]"
          >
            <LogOut size={15} /> Log out
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <p className="font-hand text-2xl text-[#A67C52] -rotate-2 mb-1">fresh from the shop</p>
        <h1 className="font-heading font-medium tracking-tight text-3xl text-[#2C402B]" data-testid="inbox-count">
          {orders
            ? showDone
              ? `${doneOrders.length} finished order${doneOrders.length === 1 ? "" : "s"}`
              : `${openOrders.length} open order${openOrders.length === 1 ? "" : "s"}`
            : "Loading…"}
        </h1>

        {orders && doneOrders.length > 0 && (
          <button
            onClick={() => setShowDone((s) => !s)}
            data-testid="toggle-finished-button"
            className="mt-4 rounded-full border-2 border-[#8AA387] text-[#576956] px-5 py-2 text-sm font-heading font-medium transition-[background-color,color,transform] duration-300 hover:bg-[#8AA387] hover:text-[#F9F6F0]"
          >
            {showDone ? `Back to open orders (${openOrders.length})` : `View finished orders (${doneOrders.length})`}
          </button>
        )}

        {orders && visible.length === 0 && (
          <div className="mt-12 text-center bg-[#E8EAE3] rounded-3xl p-12 border border-[#D3D7CF]" data-testid="orders-empty">
            <Package className="mx-auto text-[#8AA387]" size={40} />
            <p className="mt-4 text-[#576956]">
              {showDone ? "Nothing finished yet — happy stitching!" : "All caught up — no open orders right now."}
            </p>
          </div>
        )}

        <div className="mt-8 space-y-5">
          <AnimatePresence>
            {visible.map((o, i) => (
              <motion.article
                key={o.id}
                layout
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: 40 }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="bg-white rounded-3xl border border-[#D3D7CF] shadow-[0_8px_30px_rgba(44,64,43,0.06)] p-6"
                data-testid="order-row"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-heading font-medium text-lg text-[#2C402B]" data-testid="order-row-name">{o.name}</p>
                    <a href={`mailto:${o.email}`} className="text-sm text-[#8AA387] font-semibold hover:underline" data-testid="order-row-email">
                      {o.email}
                    </a>
                  </div>
                  <div className="text-right">
                    <span className="inline-block bg-[#E8EAE3] rounded-full px-4 py-1.5 font-hand text-xl text-[#C27F67]" data-testid="order-row-items">
                      {o.items}
                    </span>
                    <p className="text-xs text-[#576956]/70 mt-1.5">
                      {new Date(o.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                    </p>
                  </div>
                </div>
                {o.message && <p className="mt-4 text-sm text-[#576956] leading-relaxed border-t border-[#D3D7CF]/60 pt-4">{o.message}</p>}

                <div className="mt-5 flex flex-wrap gap-3">
                  <button
                    onClick={() => setReplyingFor(replyingFor === o.id ? null : o.id)}
                    data-testid={`order-reply-button-${o.id}`}
                    className="flex items-center gap-2 rounded-full border-2 border-[#2C402B] text-[#2C402B] px-5 py-2.5 text-sm font-heading font-medium transition-[background-color,color,transform] duration-300 hover:bg-[#2C402B] hover:text-[#F9F6F0] hover:scale-105"
                  >
                    <Mail size={15} /> Reply
                  </button>
                  {o.done ? (
                    <button
                      onClick={() => setDone(o.id, false)}
                      disabled={busy === o.id}
                      data-testid={`order-undo-button-${o.id}`}
                      className="flex items-center gap-2 rounded-full border-2 border-[#A67C52] text-[#A67C52] px-5 py-2.5 text-sm font-heading font-medium transition-[background-color,color,transform] duration-300 hover:bg-[#A67C52] hover:text-[#F9F6F0] disabled:opacity-60"
                    >
                      <Undo2 size={15} /> Move back to open
                    </button>
                  ) : (
                    <button
                      onClick={() => setDone(o.id, true)}
                      disabled={busy === o.id}
                      data-testid={`order-done-button-${o.id}`}
                      className="flex items-center gap-2 rounded-full bg-[#8AA387] text-[#F9F6F0] px-5 py-2.5 text-sm font-heading font-medium transition-[transform,background-color,box-shadow,opacity] duration-300 hover:bg-[#2C402B] hover:scale-105 hover:shadow-[0_10px_25px_rgba(44,64,43,0.2)] disabled:opacity-60"
                    >
                      <Check size={15} /> Mark as done
                    </button>
                  )}
                </div>

                <AnimatePresence>
                  {replyingFor === o.id && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                      data-testid={`reply-picker-${o.id}`}
                    >
                      <div className="mt-5 bg-[#E8EAE3]/70 rounded-2xl p-5 border border-[#D3D7CF]">
                        <p className="font-hand text-xl text-[#A67C52] mb-3">pick a sweet note to send:</p>
                        <div className="space-y-2.5">
                          {REPLY_TEMPLATES.map((t) => (
                            <button
                              key={t.key}
                              onClick={() => sendReply(o, t.key)}
                              disabled={busy === o.id}
                              data-testid={`reply-template-${t.key}-${o.id}`}
                              className="w-full text-left bg-white rounded-xl border border-[#D3D7CF] px-4 py-3 transition-[border-color,transform,box-shadow] duration-300 hover:border-[#8AA387] hover:scale-[1.01] hover:shadow-[0_6px_18px_rgba(44,64,43,0.1)] disabled:opacity-60"
                            >
                              <span className="block font-heading font-medium text-sm text-[#2C402B]">{t.label}</span>
                              <span className="block text-xs text-[#576956] mt-0.5">"{t.preview}"</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.article>
            ))}
          </AnimatePresence>
        </div>

        <Link to="/" className="inline-flex items-center gap-2 mt-10 text-sm font-semibold text-[#576956] hover:text-[#2C402B] transition-colors" data-testid="inbox-back-link">
          <ArrowLeft size={15} /> Back to the shop
        </Link>
      </main>
    </div>
  );
}
